// Editorial list page — coffee variant (formats line, credit lines, pull
// quote) and food variant (full-bleed plate, add-ons, MSG foot line).
// Port of prototype lines 179–197 and 263–286.
import { plateOf } from './data/adapt.js';
import { SIZES, srcSet } from './data/photos.js';

// Plates of pages other than the landing one carry data-src: the engine sets
// src when the page comes within 1.5 screens (after the load event). Native
// lazy loading is not trusted here — see the table tiles in ChapterOpener.
const BLANK = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
import { Accent } from './SignatureScene.jsx';

const mono = { fontFamily: "'DM Mono', 'DM Mono Fallback', ui-monospace, monospace" };
const serif = { fontFamily: "'Instrument Serif', Georgia, serif", fontStyle: 'italic', fontWeight: 400 };

function Chevron({ color }) {
  return (
    <svg width="8" height="12" viewBox="0 0 8 12" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
      <path d="M2 1l5 5-5 5" />
    </svg>
  );
}

// priority — this page is the deep-link landing: its plate photo is fetched
// first (it is the page's Largest Contentful Paint); other plates are lazy.
export default function ListPage({ pg, pal, T, lang, onOpen, priority }) {
  const isCoffee = pg.chap === 'chapCoffee';
  const h = lang === 'id' && pg.head.id ? pg.head.id : pg.head.en;
  const plate = isCoffee ? null : plateOf(pg);
  const chapter = T[pg.chap];
  const runner = T.r[pg.runner];
  // Descriptions switch with the language; names, credits and tasting notes
  // never do. Anything without a Bahasa line falls back to the English.
  const say = (en, id) => (lang === 'id' && id ? id : en);

  return (
    <section
      id={pg.id}
      data-tone={pg.tone}
      aria-label={`${runner} — ${h.pre}${h.it}`}
      style={{ position: 'relative', zIndex: 1, background: pal.bg, color: pal.ink, padding: '2.8em 1.5em 1.6em' }}
    >
      <div data-reveal data-on="0" style={{ display: 'flex', flexDirection: 'column' }}>
        <span className="beat" style={{ ...mono, fontSize: '.6875em', letterSpacing: '.14em', textTransform: 'uppercase', color: pal.accent }}>{chapter} · {runner}</span>
        <span className="beat" role="heading" aria-level={2} style={{ display: 'block', marginTop: '.3em', fontSize: '2.6em', fontWeight: 500, letterSpacing: '-.04em', lineHeight: 1, textWrap: 'balance', transitionDelay: '.08s' }}>
          {h.pre}<Accent color={pal.accent}>{h.it}</Accent>
        </span>
        {pg.lede ? (
          <p className="beat" style={{ margin: '.9em 0 0', fontSize: '1em', lineHeight: 1.45, color: pal.muted, maxWidth: '34em', textWrap: 'pretty', transitionDelay: '.2s' }}>{say(pg.lede, pg.ledeId)}</p>
        ) : null}
        {pg.pull ? (
          <div className="beat" style={{ display: 'flex', marginTop: '1.4em', padding: '1em 0 1.1em', borderTop: `1px solid ${pal.rule}`, borderBottom: `1px solid ${pal.rule}`, flexDirection: 'column', gap: '.5em', transitionDelay: '.3s' }}>
            <span style={{ ...mono, fontSize: '.625em', letterSpacing: '.14em', textTransform: 'uppercase', color: pal.accent }}>{pg.pull.label}</span>
            <span style={{ ...serif, fontSize: '1.35em', lineHeight: 1.28, textWrap: 'pretty' }}>{say(pg.pull.text, pg.pull.textId)}</span>
          </div>
        ) : null}
      </div>

      {isCoffee ? (
        <div style={{ marginTop: '1.4em', paddingBottom: '.8em', borderBottom: `1px solid ${pal.ink}` }}>
          <span style={{ ...mono, fontSize: '.75em', letterSpacing: '.04em', color: pal.muted }}>{pg.modifier}</span>
        </div>
      ) : (
        <>
          {plate ? (
            <button
              type="button"
              data-plate
              onClick={() => onOpen(pg, plate.ci, plate.li)}
              aria-label={plate.c.name}
              style={{ display: 'block', position: 'relative', width: 'calc(100% + 3em)', margin: '1.4em -1.5em 0', aspectRatio: '4 / 3', overflow: 'hidden', textAlign: 'left', color: '#f4f0e8', background: '#1a1410' }}
            >
              <img
                data-plate-img sizes={SIZES} alt="" decoding="async"
                src={priority ? plate.c.photo : BLANK} srcSet={priority ? srcSet(plate.c.photo) : undefined} fetchpriority={priority ? 'high' : undefined}
                data-src={priority ? undefined : plate.c.photo} data-srcset={priority ? undefined : srcSet(plate.c.photo)}
                style={{ position: 'absolute', left: 0, top: '-12%', width: '100%', height: '124%', objectFit: 'cover', display: 'block', willChange: 'transform' }} />
              <span aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(31,20,14,0) 45%, rgba(31,20,14,.82) 100%)' }} />
              <span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '0 1.5em 1.3em', display: 'flex', flexDirection: 'column', gap: '.45em' }}>
                {plate.c.signature ? <span style={{ ...mono, fontSize: '.625em', letterSpacing: '.14em', textTransform: 'uppercase', color: '#e8b86b' }}>◆ {T.house}</span> : null}
                <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16 }}>
                  <span style={{ fontSize: '1.75em', fontWeight: 500, letterSpacing: '-.035em', lineHeight: 1, textWrap: 'balance' }}>{plate.c.name}</span>
                  <span style={{ ...mono, fontWeight: 500, fontSize: '1.125em' }}>{plate.c.price}</span>
                </span>
              </span>
            </button>
          ) : null}
          <div style={{ marginTop: '1.4em', borderBottom: `1px solid ${pal.ink}` }} />
        </>
      )}

      {pg.cats.map((cat, ci) => (
        <div key={ci}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.35em', padding: '1.5em 0 .3em' }}>
            <span style={{ ...mono, fontSize: '.6875em', letterSpacing: '.14em', textTransform: 'uppercase', color: pal.accent }}>{cat.label}</span>
            {isCoffee && cat.lede ? <span style={{ fontSize: '.9375em', lineHeight: 1.45, color: pal.muted, textWrap: 'pretty' }}>{say(cat.lede, cat.ledeId)}</span> : null}
          </div>
          {cat.lots.map((c, li) => {
            const tappable = !!(c.credit || c.notes || c.photo);
            const Row = tappable ? 'button' : 'div';
            const name = (c.signature ? '◆ ' : '') + c.name;
            const addons = c.addons ? c.addons.map((a) => `${a.label} ${a.price}`).join(' · ') : '';
            return (
              <Row
                key={li}
                type={tappable ? 'button' : undefined}
                onClick={tappable ? () => onOpen(pg, ci, li) : undefined}
                style={{ width: '100%', textAlign: 'left', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', columnGap: 16, alignItems: 'start', padding: isCoffee ? '1em 0' : '.95em 0', borderBottom: `1px solid ${pal.rule}`, color: 'inherit', boxSizing: 'border-box' }}
              >
                <span style={{ display: 'flex', flexDirection: 'column', gap: '.35em', minWidth: 0 }}>
                  <span style={{ fontSize: '1.375em', fontWeight: 500, letterSpacing: '-.025em', lineHeight: 1.1, textWrap: 'pretty' }}>{name}</span>
                  {c.credit ? <span style={{ ...mono, fontSize: '.6875em', letterSpacing: '.01em', lineHeight: 1.45, color: pal.faint }}>{c.credit}</span> : null}
                  {c.notes ? <span style={{ fontSize: '.9375em', lineHeight: 1.45, color: pal.muted, textWrap: 'pretty' }}>{c.notes}</span> : null}
                  {addons ? <span style={{ ...mono, fontSize: '.75em', letterSpacing: '.02em', lineHeight: 1.5, color: pal.accent }}>{addons}</span> : null}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: '.15em' }}>
                  <span style={{ ...mono, fontWeight: 500, fontSize: '1.125em', fontVariantNumeric: 'tabular-nums' }}>{c.price}</span>
                  {tappable ? <Chevron color={pal.accent} /> : null}
                </span>
              </Row>
            );
          })}
        </div>
      ))}

      {pg.foot ? <div style={{ ...mono, fontSize: '.6875em', letterSpacing: '.1em', textTransform: 'uppercase', lineHeight: 1.6, color: pal.accent, paddingTop: '1.2em' }}>{T.foot}</div> : null}
      <div style={{ display: 'flex', justifyContent: 'space-between', ...mono, fontSize: '.625em', letterSpacing: '.12em', textTransform: 'uppercase', color: pal.muted, paddingTop: '1.4em' }}>
        <span>{T.r[pg.runner]}</span><span>{pg.folio}</span>
      </div>
    </section>
  );
}
